import { Container, Graphics, Text } from 'pixi.js';
import type { WheelDialOutcome, WheelWedge } from '@goodtime/shared';
import { Easings, TweenGroup } from '../../core/Tween';
import { wedgeAccentColor } from './WheelResolveDisc';

/**
 * Per-seat outcome chip — pops out radially from the seat avatar
 * after the wheel lands. Mirrors the legacy React {@code OutcomeChip}:
 * name + wedge label + payout (with coin icon glyph) inside a
 * dark roundel with a wedge-accent border.
 *
 * <p>Producer brief 2026-05-16 — chips reveal in payout-descending
 * order via staggered alpha tweens; the host scene drives the
 * sequence. Each chip's tween is owned by this widget so the host
 * just calls {@link reveal()}.
 */

const FONT_FAMILY = '"Fredoka", system-ui, -apple-system, sans-serif';

export interface WheelResolveOutcomeChipOptions {
  /** Min width for the chip body — matches legacy useWheelDims. */
  minWidth: number;
  /** Base font size for the payout digits. */
  baseFontSize: number;
}

export class WheelResolveOutcomeChip extends Container {
  private readonly bg: Graphics;
  private readonly nameText: Text;
  private readonly labelText: Text;
  private readonly payoutText: Text;
  private readonly tweens = new TweenGroup();
  private revealed = false;

  constructor(options: WheelResolveOutcomeChipOptions) {
    super();
    this.alpha = 0;
    this.scale.set(0.6);

    this.bg = new Graphics();
    this.addChild(this.bg);
    this.nameText = new Text({
      text: '',
      style: {
        fontFamily: FONT_FAMILY,
        fontSize: options.baseFontSize - 2,
        fontWeight: '700',
        fill: 0xffffff,
        letterSpacing: 1,
      },
    });
    this.nameText.anchor.set(0.5, 0);
    this.nameText.alpha = 0.55;
    this.addChild(this.nameText);
    this.labelText = new Text({
      text: '',
      style: {
        fontFamily: FONT_FAMILY,
        fontSize: options.baseFontSize,
        fontWeight: '900',
        fill: 0xffd86b,
        letterSpacing: 0.5,
      },
    });
    this.labelText.anchor.set(0.5, 0);
    this.addChild(this.labelText);
    this.payoutText = new Text({
      text: '',
      style: {
        fontFamily: FONT_FAMILY,
        fontSize: options.baseFontSize + 1,
        fontWeight: '800',
        fill: 0xffd700,
      },
    });
    this.payoutText.anchor.set(0.5, 0);
    this.addChild(this.payoutText);
  }

  setData(name: string, outcome: WheelDialOutcome): void {
    this.nameText.text = (name || '').split(' ')[0] ?? '';
    this.labelText.text = wedgeLongLabel(outcome.wedge);
    const accent = wedgeAccentColor(outcome.wedge);
    this.labelText.style.fill = accent;
    const isCoins = outcome.wedge.kind === 'COINS';
    if (isCoins) {
      this.payoutText.text = formatCompact(outcome.payout);
      this.payoutText.style.fill = outcome.payout > 0 ? 0xffd700 : 0x808080;
      this.payoutText.visible = true;
    } else {
      this.payoutText.visible = false;
    }
    this.layoutBg(accent);
  }

  reveal(): void {
    if (this.revealed) return;
    this.revealed = true;
    this.tweens.start({
      durationMs: 280,
      easing: Easings.easeOutBack,
      onUpdate: (p) => {
        this.alpha = p;
        this.scale.set(0.6 + p * 0.4);
      },
    });
  }

  hideImmediate(): void {
    this.revealed = false;
    this.tweens.cancelAll();
    this.alpha = 0;
    this.scale.set(0.6);
  }

  destroyDisplay(): void {
    this.tweens.cancelAll();
    this.removeChildren();
  }

  private layoutBg(accent: number): void {
    // Padded vertical stack — was cramped (50px tall) with the
    // payout digits clipping the bottom rounded corner. Producer
    // feedback 2026-05-17 ("Cards don't have the right padding"):
    // grow the chip to fit name + label + payout with comfortable
    // padding above/below + proper inter-row spacing. Width also
    // bumped from 110 → 124 so longer wedge labels ("EXTRA ITEM",
    // "BIG PRIZE") don't get truncated mid-glyph.
    const showPayout = this.payoutText.visible;
    const padTop = 7;
    const padBottom = 7;
    const rowGap = 2;
    const nameH = Math.ceil(this.nameText.height);
    const labelH = Math.ceil(this.labelText.height);
    const payoutH = showPayout ? Math.ceil(this.payoutText.height) : 0;
    const h = padTop + nameH + rowGap + labelH + (showPayout ? rowGap + payoutH : 0) + padBottom;
    const w = 124;
    this.bg.clear();
    this.bg
      .roundRect(-w / 2, -h / 2, w, h, 8)
      .fill({ color: 0x0a0518, alpha: 0.92 })
      .roundRect(-w / 2, -h / 2, w, h, 8)
      .stroke({ color: accent, alpha: 0.66, width: 1 });
    let cursorY = -h / 2 + padTop;
    this.nameText.x = 0;
    this.nameText.y = cursorY;
    cursorY += nameH + rowGap;
    this.labelText.x = 0;
    this.labelText.y = cursorY;
    cursorY += labelH + rowGap;
    this.payoutText.x = 0;
    this.payoutText.y = cursorY;
    // Track for host's halfHeight() — read after layout so the
    // host can position the chip correctly relative to the avatar.
    this.chipHalfHeight = h / 2;
  }

  private chipHalfHeight = 25;

  /** Used by the host to space the chip from the avatar. Reads
   *  the live measured half-height after {@link setData} has run. */
  halfHeight(): number {
    return this.chipHalfHeight;
  }
}

function wedgeLongLabel(wedge: WheelWedge): string {
  switch (wedge.kind) {
    case 'COINS':
      return `×${wedge.multiplier ?? 0}`;
    case 'BANKRUPT':
      return 'BANKRUPT';
    case 'SKIP_NEXT':
      return 'SKIP TURN';
    case 'EXTRA_ITEM':
      return 'EXTRA ITEM';
    case 'SPECIAL_PRIZE':
      return 'BIG PRIZE';
  }
}

function formatCompact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 10_000) return `${Math.round(n / 1000)}k`;
  if (n >= 1_000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}
